import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { getTournamentPublicSiteLink, getTournamentTVPublicSiteLink } from 'Src/helpers/link/link';
import { Map } from 'Src/components/Map/Map';
import './DetailsAndBooking.scss';

interface Props {
  tournament: Tournament;
  user: AppUser;
  isMapVisible?: boolean;
  onToggleMap?: () => void;
}

export const DetailsAndBooking = (props: Props) => {
  const { tournament, user } = props;

  const isMapVisible = Boolean(props.isMapVisible);
  const toggleMap = props.onToggleMap || (() => undefined);

  const startTime = propz.get(tournament, ['startTime'], '');
  const dateFormatted = startTime ? moment(startTime).format('dddd Do MMMM YYYY, h:mm a') : '';

  const postcode = String(propz.get(tournament, ['venue', 'postcode'], '') || '').trim();
  const placeName = String(propz.get(tournament, ['venue', 'placeData', 'name'], '') || '').trim();
  const venueType = String(propz.get(tournament, ['venue', 'venueType'], '') || '').trim();
  const point = propz.get(tournament, ['venue', 'point']);

  const organisers = String(propz.get(tournament, ['organisers'], '') || '').trim();
  const organisersEmail = String(propz.get(tournament, ['organisersEmail'], '') || '').trim();
  const nationalGoverningBodies = propz.get(tournament, ['nationalGoverningBodies'], []);

  const tournamentPublicSiteLink = getTournamentPublicSiteLink(tournament);
  const tournamentTVPublicSiteLink = getTournamentTVPublicSiteLink(tournament);
  const domain = propz.get(tournament, ['domain'], '');
  const publicSiteStatus = propz.get(tournament, ['publicSite', 'status'], '');
  const isPublicSiteLinkAvailable =
    domain !== '' && publicSiteStatus === PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PUBLIC_AVAILABLE;

  const renderNationalGoverningBodies = () => {
    if (!Array.isArray(nationalGoverningBodies) || nationalGoverningBodies.length === 0) {
      return <span className="eDetailsValueMuted">—</span>;
    }

    return (
      <div className="eDetailsBodiesList">
        {nationalGoverningBodies.map((item, index) => {
          const link = String(propz.get(item, ['link'], '') || '').trim();
          const picUrl = String(propz.get(item, ['picUrl'], '') || '').trim();

          if (!picUrl) {
            return null;
          }

          return (
            <a
              key={`governing-body-${index}`}
              href={link || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="eDetailsBodiesItem"
            >
              <img className="eNationalGoverningBodiesImage" src={picUrl} alt="Governing body" />
            </a>
          );
        })}
      </div>
    );
  };

  const formatVenueLabel = (): string => {
    if (venueType === 'CUSTOM') {
      if (placeName && postcode) {
        return `${placeName}, ${postcode}`;
      }

      if (placeName) {
        return placeName;
      }

      if (postcode) {
        return postcode;
      }

      return '';
    }

    let venueLabel = '';

    if (placeName) {
      venueLabel = placeName;
    } else if (postcode) {
      venueLabel = postcode;
    }

    if (venueType && venueLabel) {
      const normalizedVenueType =
        venueType === 'HOME' ? 'Home' : venueType === 'AWAY' ? 'Away' : venueType === 'NEUTRAL' ? 'Neutral' : venueType;

      return `${normalizedVenueType}: ${venueLabel}`;
    }

    return venueLabel;
  };

  const venueLabel = formatVenueLabel();

  const renderInfoRow = (label: string, value: React.ReactNode) => {
    return (
      <div className="eDetailsRow">
        <div className="eDetailsLabel">{label}</div>
        <div className="eDetailsValue">{value}</div>
      </div>
    );
  };

  return (
    <div className="bDetailsAndBooking">
      <div className="eDetailsSection">
        {renderInfoRow('Date', dateFormatted || <span className="eDetailsValueMuted">—</span>)}

        {renderInfoRow(
          'Organisers',
          organisers || organisersEmail ? (
            <div className="eDetailsInlineWrap">
              {organisers ? <span>{organisers}</span> : null}
              {organisers && organisersEmail ? <span className="eDetailsInlineDivider">·</span> : null}
              {organisersEmail ? (
                <a href={`mailto:${organisersEmail}`} target="_blank" rel="noopener noreferrer">
                  {organisersEmail}
                </a>
              ) : null}
            </div>
          ) : (
            <span className="eDetailsValueMuted">—</span>
          )
        )}

        {renderInfoRow(
          'National Governing Bodies',
          <div>
            <div className="eNationalGoverningBodiesSubtitle">
              Click on a logo to open the website and further information.
            </div>
            {renderNationalGoverningBodies()}
          </div>
        )}

        {renderInfoRow(
          'Results website',
          isPublicSiteLinkAvailable ? (
            <a href={tournamentPublicSiteLink} target="_blank" rel="noopener noreferrer">
              {tournamentPublicSiteLink}
            </a>
          ) : (
            <span className="eDetailsValueMuted">The link is not available yet.</span>
          )
        )}

        {renderInfoRow(
          'Tournament TV',
          isPublicSiteLinkAvailable ? (
            <a href={tournamentTVPublicSiteLink} target="_blank" rel="noopener noreferrer">
              {tournamentTVPublicSiteLink}
            </a>
          ) : (
            <span className="eDetailsValueMuted">The link is not available yet.</span>
          )
        )}
      </div>

      <div className="eDetailsMapSection">
        {venueLabel ? (
          <div className="eDetailsLocationBlock">
            <div className="eDetailsLocationHeader">
              <div className="eDetailsMapTitle">Venue</div>
              {point ? (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleMap}>
                  {isMapVisible ? 'Hide map' : 'Show map'}
                </button>
              ) : null}
            </div>

            <div className="eDetailsLocationText">{venueLabel}</div>
          </div>
        ) : null}

        {point ? (
          <>
            <div className="eDetailsLocationHeader eDetailsLocationHeaderSecondary">
              <div className="eDetailsMapTitle">Location</div>
            </div>

            {isMapVisible ? (
              <Map point={point} customStylingClass="eTournamentDetailsMap" isMarkerDraggable={false} />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};

DetailsAndBooking.displayName = 'DetailsAndBooking';



